import { Link } from "react-router-dom";
import { Calendar, MapPin, Building, ArrowLeft, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useLanguage } from "@/contexts/LanguageContext";
import { useTenders } from "@/hooks/api/use-tenders";
import { useAdminTenders } from "@/hooks/api/use-admin";
import { useAuth } from "@/contexts/AuthContext";

const Tenders = () => {
  const { t, isRTL, language } = useLanguage();
  const ArrowIcon = isRTL ? ArrowLeft : ArrowRight;
  const { user } = useAuth();
  const isAdmin = user?.type === 'admin';
  
  // Use admin APIs if admin, client APIs if client, or no API if not logged in
  const { data: clientData, isLoading: clientLoading, error: clientError } = useTenders({ page: 1 }, { enabled: !!user && !isAdmin });
  const { data: adminData, isLoading: adminLoading, error: adminError } = useAdminTenders({ page: 1 }, { enabled: !!user && isAdmin });
  
  const data = isAdmin ? adminData : clientData;
  const isLoading = isAdmin ? adminLoading : clientLoading;
  const error = isAdmin ? adminError : clientError;
  
  const tenders = data?.data?.data || [];
  return (
    <div className="py-16">
      {/* Hero */}
      <section className="py-20 bg-muted">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-6 animate-fade-up">
              {t('tenders.available')}
            </h1>
            <p className="text-lg text-muted-foreground animate-fade-up stagger-1">
              {t('tenders.browse')}
            </p>
          </div>
        </div>
      </section>

      {/* Tenders List */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          {isLoading ? (
            <div className="grid gap-6">
              {[1, 2, 3, 4].map((i) => (
                <Skeleton key={i} className="h-32 rounded-2xl" />
              ))}
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground mb-4">{t('common.error') || 'حدث خطأ أثناء تحميل المناقصات'}</p>
            </div>
          ) : tenders.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground mb-4">
                {user ? (t('tenders.noTenders') || 'لا توجد مناقصات متاحة') : t('tenders.registerMessage')}
              </p>
              {!user && (
                <Button size="lg" asChild>
                  <Link to="/auth?mode=register">
                    {t('tenders.registerNow')}
                    <ArrowIcon size={18} />
                  </Link>
                </Button>
              )}
            </div>
          ) : (
            <>
              <div className="grid gap-6">
                {tenders.map((tender) => {
                  const title = typeof tender.title === 'string' ? tender.title : tender.title[language] || tender.title.en;
                  const category = tender.category ? (typeof tender.category === 'string' ? tender.category : tender.category[language] || tender.category.en) : '';
                  const location = tender.location ? (typeof tender.location === 'string' ? tender.location : tender.location[language] || tender.location.en) : '';
                  
                  return (
                    <div
                      key={tender.id}
                      className="bg-card p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-border"
                    >
                      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-3">
                            <Badge variant={tender.status === "active" ? "default" : "secondary"}>
                              {tender.status === "active" ? t('tenders.open') : t('tenders.closed')}
                            </Badge>
                            {category && <Badge variant="outline">{category}</Badge>}
                          </div>
                          <h3 className="text-xl font-bold text-foreground mb-3">
                            {title}
                          </h3>
                          <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                            {location && (
                              <span className="flex items-center gap-1">
                                <MapPin size={16} />
                                {location}
                              </span>
                            )}
                            {tender.deadline && (
                              <span className="flex items-center gap-1">
                                <Calendar size={16} />
                                {t('tenders.public.deadline')}: {tender.deadline}
                              </span>
                            )}
                            {category && (
                              <span className="flex items-center gap-1">
                                <Building size={16} />
                                {category}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-3">
                          {user ? (
                            <Button variant="outline" asChild>
                              <Link to={`/dashboard/tenders/${tender.id}`}>
                                {t('tenders.viewDetails')}
                                <ArrowIcon size={16} />
                              </Link>
                            </Button>
                          ) : (
                            <Button variant="outline" asChild>
                              <Link to="/auth">
                                {t('tenders.viewDetails')}
                                <ArrowIcon size={16} />
                              </Link>
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {!user && (
                <div className="text-center mt-12">
                  <p className="text-muted-foreground mb-4">
                    {t('tenders.registerMessage')}
                  </p>
                  <Button size="lg" asChild>
                    <Link to="/auth?mode=register">
                      {t('tenders.registerNow')}
                      <ArrowIcon size={18} />
                    </Link>
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </div>
  );
};

export default Tenders;
